import { StoreInfo } from '../models';

/**
 * The signed-in session, persisted to `sessionStorage` so a browser refresh
 * keeps the user authenticated instead of bouncing back to the login screen.
 *
 * `sessionStorage` (not `localStorage`) is deliberate: the session lives for
 * the life of the tab and is cleared when the tab closes, matching a
 * per-terminal kiosk session.
 */
export interface PersistedSession {
  token: string;
  accountId: string;
  storeId: string;
  terminalId: string;
  storeInfo: StoreInfo;
  assetName: string;
}

const SESSION_KEY = 'zenoms.session';

/** Base64url-decodes and parses a JWT payload; null if the token is malformed. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * True when the JWT carries an `exp` claim that is in the past.
 * A token with no decodable `exp` is treated as NOT expired — the server
 * stays the source of truth and a 401 will trigger logout at call time.
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload?.['exp'];
  if (typeof exp !== 'number') return false;
  // `exp` is in seconds; compare against now.
  return exp * 1000 <= Date.now();
}

/** Persists the session for the current tab. */
export function saveSession(session: PersistedSession): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage unavailable (private mode / quota) — session just won't survive refresh.
  }
}

/**
 * Loads a still-valid session, or null when there's none, it's corrupt, or its
 * token has already expired (in which case the stored session is cleared).
 */
export function loadSession(): PersistedSession | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let session: PersistedSession;
  try {
    session = JSON.parse(raw) as PersistedSession;
  } catch {
    clearSession();
    return null;
  }

  if (!session?.token || !session.accountId) {
    clearSession();
    return null;
  }

  if (isTokenExpired(session.token)) {
    clearSession();
    return null;
  }

  return session;
}

/** Reads just the token for the current tab (null if none or expired). */
export function getSessionToken(): string | null {
  return loadSession()?.token ?? null;
}

/** Clears the persisted session. */
export function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}
